import { useState } from 'react'
import type { ReportOverviewRow } from '@headline/schemas'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { FilterPills } from '@/components/FilterPills'
import { LibraryTable, type LibraryColumn } from '@/components/LibraryTable'
import { PageHeader } from '@/components/PageHeader'
import { ListChecks, Table2 } from 'lucide-react'
import { AssignedIA } from '@/components/reports/AssignedIA'
import { AutoSendSwitches } from '@/components/reports/AutoSendSwitches'
import { DeliveryQueue } from '@/components/reports/DeliveryQueue'
import {
  ReportConfigHeader,
  ReportNameLink,
  StopRowClick,
} from '@/components/reports/ReportNameLink'
import { NewReportDialog } from '@/components/reports/NewReportDialog'
import { SendModeBadge } from '@/components/reports/SendModeBadge'
import { IssueActions } from '@/components/issues/IssueActions'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { accountsQuery, reportsOverviewQuery, sessionQuery } from '@/lib/queries'
import { defaultWhose, isOwnedBy } from '@/lib/report-scope'
import { isStaff } from '@/lib/viewer'
import { cn } from '@/lib/utils'
import { SendTimeCell } from '@/components/reports/SendTimeCell'

/**
 * The page's one set of controls, as search params so a filtered view is a link someone can send.
 *
 * There used to be two ways to "see everything" — Today / All reports in the header and Mine / All
 * inside the queue — and the header's Today badge counted everyone while the tiles counted only
 * Mine, so one screen showed 262 and 0 (CLI-854). Now one row scopes both views the same way.
 * `whose` has no default here because the right one depends on who is looking (see `defaultWhose`).
 */
const searchSchema = z.object({
  view: z.enum(['today', 'all']).default('today').catch('today'),
  whose: z.enum(['mine', 'everyone']).optional().catch(undefined),
  mode: z.enum(['any', 'auto', 'manual']).default('any').catch('any'),
  status: z.enum(['any', 'active', 'paused']).default('any').catch('any'),
})
type ReportsSearch = z.infer<typeof searchSchema>

/**
 * The queue refreshes itself: it is a shift view people keep open all day, and a send that went
 * out at 9:00 should not still read Due at noon. Cheap — one indexed read per report's day.
 */
const OVERVIEW_REFRESH_MS = 60_000

/**
 * The Clipbook-internal, org-wide Reports table — one row per report across every account, the
 * analogue of the prototype's "Report Configuration" (webapp2.0-prototype /internal/subscriptions).
 * An IA works across many clients, so a per-workspace list forces a hop through each account; this
 * is the cross-account counterpart they scan and jump from.
 *
 * Not a data leak: the server already scopes `reportsOverviewQuery` to the accounts the session may
 * view (internal admins bypass), so this shows exactly what the per-account Products → Reports views
 * would, gathered into one table. A row opens the report in its own account context; its Edit
 * actions open the issue editor (CLI-695).
 *
 * Creating a report lives here too, not only on an account's own Reports page: a report binds to a
 * sub-account, so the dialog picks account → client → name, then opens the new report's config.
 * Same `useCreateReport` the account page uses — one create path, two entry points.
 *
 * The table chrome lives in LibraryTable — the standard internal-library shape (pills + search,
 * in-table empty states) — so Library / RTAs / Analytics get the same cross-account view by
 * supplying their own columns; this page only owns the Reports columns.
 */
export const Route = createFileRoute('/app/reports')({
  validateSearch: searchSchema,
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(reportsOverviewQuery),
      context.queryClient.ensureQueryData(accountsQuery),
      context.queryClient.ensureQueryData(sessionQuery),
    ]),
  component: ReportsOverviewPage,
})

const REPORT_COLUMNS: Array<LibraryColumn<ReportOverviewRow>> = [
  {
    key: 'name',
    header: <ReportConfigHeader />,
    cell: (report) => (
      <ReportNameLink accountId={report.accountId} reportId={report.id} name={report.name} />
    ),
  },
  {
    key: 'account',
    header: 'Account',
    cell: (report) => report.accountName,
    cellClassName: 'text-muted-foreground',
  },
  {
    key: 'status',
    header: 'Status',
    cell: (report) => (
      <Badge variant={report.status === 'active' ? 'secondary' : 'outline'}>{report.status}</Badge>
    ),
  },
  {
    key: 'owners',
    header: 'IA',
    cell: (report) => <AssignedIA owners={report.owners} />,
  },
  {
    key: 'sendMode',
    header: 'Mode',
    cell: (report) => <SendModeBadge mode={report.sendMode} />,
  },
  {
    key: 'sendTime',
    header: 'Send time',
    cell: (report) => (
      <SendTimeCell
        sendTimes={report.sendTimes}
        timezone={report.timezone}
        schedule={report.schedule}
      />
    ),
  },
  {
    key: 'edit',
    header: 'Drafts',
    // Centred: its contents are two buttons rather than a value.
    headClassName: 'text-center',
    cell: (report) => (
      <StopRowClick>
        <div className="flex justify-center">
          <IssueActions report={report} />
        </div>
      </StopRowClick>
    ),
  },
]

// The client has no column any more but is still searchable: "Project Emerald" is how an
// associate looks for a report whose name doesn't contain it.
const searchText = (report: ReportOverviewRow) =>
  [
    report.name,
    report.accountName,
    report.subaccountName,
    // "what is Ana on today" is the other way into this table.
    ...report.owners.map((owner) => `${owner.name ?? ''} ${owner.email ?? ''}`),
  ].join(' ')

function ReportsOverviewPage() {
  const navigate = useNavigate({ from: Route.fullPath })
  const search = Route.useSearch()
  const { data } = useSuspenseQuery({
    ...reportsOverviewQuery,
    refetchInterval: OVERVIEW_REFRESH_MS,
  })
  const { data: accountsData } = useSuspenseQuery(accountsQuery)
  const { data: session } = useSuspenseQuery(sessionQuery)
  const [creating, setCreating] = useState(false)
  const viewerEmail = session.user?.email ?? null
  // Today is the shift; All reports is the configuration list. Two jobs, one page — a report that
  // isn't due today (paused, delivered by the portal, or not scheduled) still has to be findable.
  const { view, mode, status } = search
  const whose = search.whose ?? defaultWhose(data.reports, viewerEmail, isStaff(session.user))
  const setSearch = (patch: Partial<ReportsSearch>) => {
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })
  }
  const activeCount = data.reports.filter((report) => report.status === 'active').length

  // Whose and mode scope both views; status only means something in the configuration list,
  // because the queue already holds active reports alone.
  const scoped = data.reports.filter(
    (report) =>
      (whose === 'everyone' || isOwnedBy(report, viewerEmail)) &&
      (mode === 'any' || report.sendMode === mode),
  )
  const rows = scoped.filter((report) => status === 'any' || report.status === status)

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader
          title="Reports"
          description={
            view === 'today'
              ? 'What Headline sends today, in the order it goes out.'
              : `Every report across all accounts — scan, search, and open one in its account. ${data.reports.length} report${data.reports.length === 1 ? '' : 's'} · ${activeCount} active.`
          }
        />
        <div className="flex items-center gap-2">
          {isStaff(session.user) && <AutoSendSwitches />}
          <Button disabled={accountsData.accounts.length === 0} onClick={() => setCreating(true)}>
            New report
          </Button>
        </div>
      </div>

      <ReportsControls
        view={view}
        whose={whose}
        mode={mode}
        status={status}
        setSearch={setSearch}
      />

      {view === 'today' ? (
        <DeliveryQueue reports={scoped} mineOnly={whose === 'mine'} />
      ) : (
        <LibraryTable
          rows={rows}
          filtered={whose === 'mine' || mode !== 'any' || status !== 'any'}
          onRowClick={(report) =>
            void navigate({
              to: '/app/accounts/$accountId/reports/$reportId',
              params: { accountId: report.accountId, reportId: report.id },
            })
          }
          columns={REPORT_COLUMNS}
          rowKey={(report) => report.id}
          searchText={searchText}
          searchPlaceholder="Search reports…"
          minWidthClass="min-w-[720px]"
          emptyAll="No reports yet. Create one with the button above, or from an account's Reports page."
          emptyFiltered={
            whose === 'mine' ? 'No reports assigned to you match.' : 'No reports match.'
          }
        />
      )}

      {creating && (
        <NewReportDialog accounts={accountsData.accounts} onClose={() => setCreating(false)} />
      )}
    </div>
  )
}

/** The search with `whose` resolved — the page fills it from `defaultWhose` when the link has none. */
type ResolvedSearch = Omit<ReportsSearch, 'whose'> & {
  whose: NonNullable<ReportsSearch['whose']>
}

/** The one row of controls that scopes both views: view, whose, mode, and (All reports) status. */
function ReportsControls({
  view,
  whose,
  mode,
  status,
  setSearch,
}: ResolvedSearch & { setSearch: (patch: Partial<ReportsSearch>) => void }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div
        role="group"
        aria-label="Reports view"
        className="flex items-center rounded-md border border-border bg-muted/40 p-0.5"
      >
        {(
          [
            { key: 'today', label: 'Today', Icon: ListChecks },
            { key: 'all', label: 'All reports', Icon: Table2 },
          ] as const
        ).map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            aria-pressed={view === key}
            onClick={() => setSearch({ view: key })}
            className={cn(
              'flex items-center gap-1 rounded px-2.5 py-1 text-xs font-medium transition-colors',
              view === key
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="size-3.5" />
            {label}
          </button>
        ))}
      </div>
      <span className="mx-1 h-4 w-px bg-border" aria-hidden />
      <div role="group" aria-label="Whose reports" className="flex items-center gap-2">
        <FilterPills
          options={[
            { value: 'mine', label: 'Mine' },
            { value: 'everyone', label: 'Everyone' },
          ]}
          value={whose}
          onChange={(value) => setSearch({ whose: value })}
        />
      </div>
      <span className="mx-1 h-4 w-px bg-border" aria-hidden />
      <div role="group" aria-label="Send mode" className="flex items-center gap-2">
        <FilterPills
          options={[
            { value: 'any', label: 'Any mode' },
            { value: 'auto', label: 'Auto' },
            { value: 'manual', label: 'Manual' },
          ]}
          value={mode}
          onChange={(value) => setSearch({ mode: value })}
        />
      </div>
      {view === 'all' && (
        <>
          <span className="mx-1 h-4 w-px bg-border" aria-hidden />
          <div role="group" aria-label="Report status" className="flex items-center gap-2">
            <FilterPills
              options={[
                { value: 'any', label: 'All statuses' },
                { value: 'active', label: 'Active' },
                { value: 'paused', label: 'Paused' },
              ]}
              value={status}
              onChange={(value) => setSearch({ status: value })}
            />
          </div>
        </>
      )}
    </div>
  )
}
